import React, { useEffect } from "react";
import ShieldFooterIMG from "../../assets/shield.png";
import "./privacy-policy.css";

const pageTitle = "Road Home Program Admin Privacy Policy";
const pageDescription =
  "Privacy policy for the Road Home Program internal admin app and protected AAR photo workflow.";
const canonicalUrl = "https://roadhome.io/privacy-policy";
const supportEmail = "Chris Miller christopher_miller@rush.edu";

const sections = [
  {
    title: "Information the app accesses",
    paragraphs: [
      "When an authorized staff user uses the AAR photo workflow, the app uploads newly selected outreach/event images to private Road Home Program storage.",
      "During the historical transition, the app may access Google Drive only as needed to preview or manage legacy AAR photo files and folders.",
      "The app stores limited photo metadata in the Road Home Program database, including storage provider/location fields, filename, MIME type, file size, upload timestamp, and related AAR/event IDs. Historical Google records may also include Drive file/folder IDs, dimensions, thumbnail links, web view links, and checksums when available.",
    ],
  },
  {
    title: "How the information is used",
    paragraphs: [
      "Private photo storage and legacy Google Drive access support internal outreach event documentation and after-action-report photo management.",
      "The app does not use Google Drive data for advertising, user profiling, sale of data, or unrelated analytics.",
      "The app is not intended to read, download, delete, or modify unrelated Google Drive files. Google Drive access is limited to designated legacy AAR photo records and related folder/file management.",
    ],
  },
  {
    title: "Data storage",
    paragraphs: [
      "New AAR outreach/event photo files are stored in private Supabase Storage. Historical AAR photo files may remain in Google Drive during migration. Photo metadata is stored in the Road Home Program database to support protected internal views and reporting.",
    ],
  },
  {
    title: "Access controls",
    paragraphs: [
      "The app is available only to authorized staff users. Staff access is controlled through authenticated accounts and role-based permissions.",
    ],
  },
  {
    title: "Data sharing",
    paragraphs: [
      "The Road Home Program does not sell Google user data. Google Drive files and metadata are used for internal Road Home Program administrative purposes and may be accessible to authorized staff or approved organizational systems involved in outreach operations, reporting, or compliance.",
    ],
  },
  {
    title: "Retention",
    paragraphs: [
      "Uploaded files and related metadata may be retained according to Road Home Program operational, administrative, and record-retention needs. Files may be removed from private storage, legacy Google Drive, or the database when no longer needed or when required by organizational policy.",
    ],
  },
  {
    title: "Security",
    paragraphs: [
      "The app uses authenticated access and server-side controls to limit use of the AAR photo upload workflow to authorized staff. Secrets, tokens, and credentials are not intentionally exposed to end users.",
    ],
  },
  {
    title: "User control",
    paragraphs: [
      "The Google account connected to the legacy AAR Drive integration may revoke Google access through its account permissions. If access is revoked, legacy Google-backed AAR photo previews or management may stop working until access is reauthorized; new private-storage uploads do not require Google access.",
    ],
  },
  {
    title: "Contact",
    paragraphs: [
      "For questions about this privacy policy or the Road Home Program Admin app, contact:",
      supportEmail,
    ],
  },
];

function setMetaTag({ selector, create, attributes }) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement(create);
    document.head.appendChild(tag);
  }

  for (const [name, value] of Object.entries(attributes)) {
    tag.setAttribute(name, value);
  }
}

function usePrivacyPolicyMetadata() {
  useEffect(() => {
    document.title = pageTitle;

    setMetaTag({
      selector: 'meta[name="description"]',
      create: "meta",
      attributes: { name: "description", content: pageDescription },
    });
    setMetaTag({
      selector: 'link[rel="canonical"]',
      create: "link",
      attributes: { rel: "canonical", href: canonicalUrl },
    });
  }, []);
}

export default function PrivacyPolicyPage() {
  usePrivacyPolicyMetadata();

  return (
    <div className="privacy-policy-page">
      <main className="privacy-policy-shell">
        <section className="privacy-policy-hero" aria-labelledby="privacy-policy-title">
          <p className="privacy-policy-eyebrow">Road Home Program Admin App</p>
          <h1 id="privacy-policy-title">Road Home Program Admin Privacy Policy</h1>
          <p className="privacy-policy-effective-date">Effective date: June 24, 2026</p>
          <p className="privacy-policy-intro">
            The Road Home Program Admin app is an internal administrative tool used by authorized
            Road Home Program staff to support outreach event operations, after-action reporting,
            and related administrative workflows.
          </p>
          <p className="privacy-policy-intro">
            This policy describes how the app uses Google Drive access for after-action-report
            photo uploads.
          </p>
        </section>

        <div className="privacy-policy-content">
          {sections.map((section) => (
            <section className="privacy-policy-section" key={section.title}>
              <h2>{section.title}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}
        </div>
      </main>

      <footer className="privacy-policy-footer tdp-footer" role="contentinfo">
        <div className="tdp-footer__inner">
          <div className="tdp-footer__brand">
            <img
              src={ShieldFooterIMG}
              alt=""
              className="tdp-footer__shield"
              aria-hidden="true"
            />
            <div className="tdp-footer__brandtext">
              <div className="tdp-footer__title">Road Home Program</div>
              <div className="tdp-footer__org">Center for Veterans and Their Families at Rush</div>
              <div className="tdp-footer__contact">
                <a href="https://roadhomeprogram.org/contact-us/">Contact Road Home Program</a>
              </div>
            </div>
          </div>

          <nav className="tdp-footer__nav" aria-label="Footer">
            <a href="https://roadhomeprogram.org/get-care/">Get Care</a>
            <a href="https://roadhomeprogram.org/outreach-and-events/">Outreach and Events</a>
            <a href="https://roadhomeprogram.org/contact-us/">General Information</a>
            <a href="/privacy-policy" aria-current="page">
              Admin Privacy Policy
            </a>
          </nav>

          <div className="tdp-footer__legal">
            &copy; {new Date().getFullYear()} Road Home Program at Rush.
          </div>
        </div>
      </footer>
    </div>
  );
}
